// hooks/useDraftAnswer.js
import { useEffect, useRef, useState, useCallback } from "react";
import { pushActivity } from "../utils/activity";
import { API_BASE } from "../config/apiBase";

export default function useDraftAnswer({
    token,
    apiBase = API_BASE,
    questionSlug,
    debounceMs = 600,
    disableAutoSave = false,
}) {
    const [body, setBody] = useState("");
    const [loading, setLoading] = useState(!!token && !!questionSlug);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const [lastSavedBody, setLastSavedBody] = useState("");

    const saveTimer = useRef(null);
    const acRef = useRef(null);

    const lastSavedBodyRef = useRef("");
    const latestBodyRef = useRef("");
    useEffect(() => {
        latestBodyRef.current = body;
    }, [body]);

    // Load current draft
    useEffect(() => {
        if (!token || !questionSlug) return;

        setLoading(true);
        setError(null);

        acRef.current?.abort();
        const ac = new AbortController();
        acRef.current = ac;

        (async () => {
            try {
                let res = await fetch(
                    `${apiBase}/me/answers?slug=${encodeURIComponent(questionSlug)}`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                        signal: ac.signal,
                    }
                );

                let draftBody = "";
                let draftUpdatedAt = null;
                if (res.ok) {
                    const rows = await res.json();
                    const row = Array.isArray(rows) ? rows[0] : rows;
                    draftBody = row?.body ?? "";
                    draftUpdatedAt = row?.updated_at ?? null;
                } else {
                    res = await fetch(`${apiBase}/me/answers`, {
                        headers: { Authorization: `Bearer ${token}` },
                        signal: ac.signal,
                    });
                    if (res.ok) {
                        const rows = await res.json();
                        const row = rows.find(
                            (r) => r.question_slug === questionSlug
                        );
                        draftBody = row?.body ?? "";
                        draftUpdatedAt = row?.updated_at ?? null;
                    }
                }

                setBody(draftBody);
                lastSavedBodyRef.current = draftBody;
                setLastSavedBody(draftBody);
                setLastSavedAt(
                    draftUpdatedAt ? new Date(draftUpdatedAt) : null
                );
            } catch (e) {
                if (e.name !== "AbortError") setError(e);
            } finally {
                setLoading(false);
            }
        })();

        return () => ac.abort();
    }, [token, questionSlug, apiBase]);

    const saveNow = useCallback(async () => {
        if (!token || !questionSlug) return;

        const currentBody = latestBodyRef.current ?? "";
        if (currentBody === lastSavedBodyRef.current) return; // no-op

        setSaving(true);
        try {
            const res = await fetch(`${apiBase}/me/answers`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    question_slug: questionSlug,
                    body: currentBody,
                }),
            });
            if (!res.ok) throw new Error(`Save failed: ${res.status}`);

            lastSavedBodyRef.current = currentBody;
            setLastSavedBody(currentBody);
            setLastSavedAt(new Date());
            setError(null);
            pushActivity({
                type: "draft",
                question_slug: questionSlug,
                body_length: currentBody.length,
            });
        } catch (e) {
            setError(e);
        } finally {
            setSaving(false);
        }
    }, [token, questionSlug, apiBase]);

    // Debounced save
    useEffect(() => {
        if (!token || !questionSlug) return;
        if (loading) return;
        if (disableAutoSave) return;

        // no changes since last save
        if (body === lastSavedBodyRef.current) return;

        clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(saveNow, debounceMs);

        return () => clearTimeout(saveTimer.current);
    }, [body, token, questionSlug, apiBase, loading, debounceMs, saveNow]);

    // Flush on tab close / refresh
    useEffect(() => {
        if (disableAutoSave) return undefined;
        const onBeforeUnload = () => {
            // best effort (async not guaranteed to finish)
            // but it often helps in normal navigations
            saveNow();
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [saveNow, disableAutoSave]);

    // Flush on unmount (route change)
    useEffect(() => {
        if (disableAutoSave) return undefined;
        return () => {
            clearTimeout(saveTimer.current);
            // fire and forget
            saveNow();
        };
    }, [saveNow, disableAutoSave]);

    return {
        body,
        setBody,
        loading,
        saving,
        error,
        lastSavedAt,
        lastSavedBody,
        saveNow,
    };
}
